import React, { useEffect, useRef, useState } from 'react';
import { Header, HeaderRef } from '@/components/Header';
import { useUserPrefs } from '@/context/UserPrefsContext';
import { uiText } from '@/i18n/locales';
import { NewsList } from '@/components/NewsList';
import { Spinner } from '@fluentui/react-components';
import type { NewsItem } from '@/types';
import { getCityNewsByDate, getCityNewsDates } from '@/services/newsService';
import { AdBanner } from '@/components/AdBanner';
import { Footer } from '@/components/Footer';
import { useLocation } from 'react-router-dom';

/* ---------- Helpers ---------- */
function toDateKey(timestamp: number) {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

export const CityNewsPage: React.FC = () => {
  const { prefs } = useUserPrefs();
  const t = uiText[prefs.language] ?? uiText.en;
  const location = useLocation();
  const headerRef = useRef<HeaderRef>(null);
  const [items, setItems] = useState<NewsItem[]>([]);
  const [keys, setKeys] = useState<string[]>([]);
  const [dateIndex, setDateIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const todayKey = toDateKey(Date.now());
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isEvents = location.pathname.endsWith('/events');
  const handleLoginOpen = () => headerRef.current?.openLogin();

  const indexName = isEvents ? 'eventDateIndex' : 'dateIndex';
  const category = isEvents ? 'events' : 'news';

  /* ---------- Initial Load ---------- */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      if (!prefs.country || !prefs.state || !prefs.city) return;

      try {
        const dateKeys = await getCityNewsDates({
          country: prefs.country,
          state: prefs.state,
          city: prefs.city,
          language: prefs.language,
          indexName,
        });

        if (!dateKeys.includes(todayKey)) dateKeys.unshift(todayKey);

        const todayItemsRaw = await getCityNewsByDate({
          country: prefs.country,
          state: prefs.state,
          city: prefs.city,
          language: prefs.language,
          dateKey: todayKey,
          category,
        });

        if (cancelled) return;

        const todayItems = todayItemsRaw.map(item => ({
          ...item,
          dateKey: todayKey,
        }));

        setItems(todayItems);
        setKeys(dateKeys);
        setDateIndex(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [prefs.country, prefs.state, prefs.city, prefs.language, todayKey, indexName, category]);

  /* ---------- Load Older News ---------- */
  const loadMore = async () => {
    if (loadingMore) return;
    const nextIndex = dateIndex + 1;
    if (nextIndex >= keys.length) return;
    if (!prefs.country || !prefs.state || !prefs.city) return;

    const dateKey = keys[nextIndex];
    setLoadingMore(true);

    try {
      const olderRaw = await getCityNewsByDate({
        country: prefs.country,
        state: prefs.state,
        city: prefs.city,
        language: prefs.language,
        dateKey,
        category,
      });

      const older = olderRaw.map(item => ({
        ...item,
        dateKey,
      }));

      setItems(prev => [...prev, ...older]);
      setDateIndex(nextIndex);
    } catch (err) {
      console.error('CityNews loadMore error', err);
    } finally {
      setLoadingMore(false);
    }
  };

  /* ---------- Infinite Scroll ---------- */
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root: null, rootMargin: '300px', threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [dateIndex, keys, loadingMore]);

  return (
    <div>
      <Header ref={headerRef} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 12 }}>
        <h2>
          {prefs.city} {isEvents ? 'Events' : 'News'}
        </h2>

        {items.length > 3 && (
          <div style={{ marginBottom: '16px' }}>
            <AdBanner adSlot="1121804425" />
          </div>
        )}

        {loading ? (
          <Spinner label={t.loading ?? 'Loading...'} />
        ) : items.length > 0 ? (
          /* ✅ Pass all items to a single NewsList */
          <NewsList
            items={items}
            simple={prefs.viewMode === 'simple'}
            routineKey={isEvents ? category : prefs.city}
            dateKey={todayKey}
            onLoginClick={handleLoginOpen}
          />
        ) : (
          <div style={{ fontStyle: 'italic', color: '#666' }}>No posts yet</div>
        )}

        {dateIndex < keys.length - 1 && (
          <div
            ref={loadMoreRef}
            style={{
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {loadingMore && <Spinner size="small" />}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};